import { useState, useEffect } from 'react';
import { apiKeyService } from '../services/apiKeyService';

// Interface pour une seule clé API, enrichie
interface ApiKey {
  id?: string | number;
  name?: string; // Ajout de name pour recherche large
  service?: string;
  provider?: string;
  keyValue?: string;
  apiKey?: string;
  value?: string;
  key?: string;
}

export type PaymentApiKeysState = {
  stripePublicKey: string | null;
  paypalClientId: string | null;
  openAIApiKey: string | null;
  swiklyApiKey: string | null;
  openWeatherApiKey: string | null;
  isLoading: boolean;
  error: string | null;
  hasStripeKey: boolean;
  hasPaypalKey: boolean;
  hasOpenAIKey: boolean;
  hasSwiklyKey: boolean;
  hasOpenWeatherKey: boolean;
};

// Fonction utilitaire pour extraire la valeur de la clé depuis divers champs possibles
function getKeyValue(rec: ApiKey | undefined | null): string | null {
  if (!rec) return null;
  return rec.keyValue || rec.apiKey || rec.value || rec.key || (rec as any).token || null;
}

const extractStripePublicKey = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase().startsWith('pk_')) return trimmed;
  const firstPart = trimmed.split(':')[0].trim();
  if (firstPart.toLowerCase().startsWith('pk_')) return firstPart;
  const match = trimmed.match(/pk_(live|test)_[a-z0-9]+/i);
  return match ? match[0] : null;
};

export function usePaymentApiKeys(): PaymentApiKeysState {
  const CACHE_KEY = 'payment_api_keys_cache_v1';
  const CACHE_TTL_MS = 10 * 60 * 1000;

  const [state, setState] = useState<PaymentApiKeysState>({
    stripePublicKey: null,
    paypalClientId: null,
    openAIApiKey: null,
    swiklyApiKey: null,
    openWeatherApiKey: null,
    isLoading: true,
    error: null,
    hasStripeKey: false,
    hasPaypalKey: false,
    hasOpenAIKey: false,
    hasSwiklyKey: false,
    hasOpenWeatherKey: false,
  });

  useEffect(() => {
    let aborted = false;
    const now = Date.now();
    try {
      const cachedRaw = localStorage.getItem(CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.ts && now - cached.ts < CACHE_TTL_MS && cached.data) {
          setState({
            ...cached.data,
            isLoading: false,
            error: null
          });
        }
      }
    } catch {
      // ignore cache errors
    }

    (async () => {
      try {
        const cachedData = (() => {
          try {
            const cachedRaw = localStorage.getItem(CACHE_KEY);
            if (!cachedRaw) return false;
            const cached = JSON.parse(cachedRaw);
            return cached && cached.ts && now - cached.ts < CACHE_TTL_MS && cached.data ? cached.data : null;
          } catch {
            return null;
          }
        })();
        if (cachedData) {
          if (!aborted) {
            setState({
              ...cachedData,
              isLoading: false,
              error: null
            });
          }
          return;
        }
        setState((s) => ({ ...s, isLoading: true, error: null }));

        // Récupération des clés (gestion format tableau ou objet { apiKeys: [] })
        const response: any = await apiKeyService.getApiKeys();
        const apiKeys: ApiKey[] = Array.isArray(response) ? response : (response.apiKeys || []);

        const lc = (s?: string) => (s || '').toLowerCase();

        // 1. Recherche stricte par couple service/provider
        const findFirst = (...pairs: [string, string][]) =>
          apiKeys.find((k) => pairs.some(([svc, prov]) => lc(k.service) === lc(svc) && lc(k.provider) === lc(prov)));

        // 2. Recherche souple par nom ou service (contient le terme)
        const findLoose = (term: string) => 
            apiKeys.find(k => {
                const n = lc(k.name);
                const s = lc(k.service);
                return n === term || s === term || n.includes(term) || s.includes(term);
            });

        // --- Récupération des clés ---
        
        const stripe = findFirst(['stripe', 'stripe'], ['Stripe', 'stripe']);
        const paypal = findFirst(['paypal', 'paypal'], ['PayPal', 'paypal']);
        const openai = findFirst(['OpenAI', 'other'], ['OpenAI', 'openai'], ['openai', 'openai'], ['openai', 'other']);
        const swikly = findFirst(['Swikly', 'swikly'], ['swikly', 'swikly']);
        
        // OpenWeather : Stratégie stricte PUIS stratégie large
        let openweather = findFirst(
            ['OpenWeather', 'openweather'], 
            ['openweather', 'openweather'], 
            ['OpenWeatherMap', 'openweathermap'], 
            ['openweathermap', 'openweathermap']
        );

        // Si non trouvé via provider, on cherche tout ce qui ressemble à "openweather" dans le nom ou le service
        if (!openweather) {
            openweather = findLoose('openweather') || findLoose('openweathermap');
        }

        const stripeVal = extractStripePublicKey(getKeyValue(stripe));
        const paypalVal = getKeyValue(paypal);
        const openaiVal = getKeyValue(openai);
        const swiklyVal = getKeyValue(swikly);
        const openweatherVal = getKeyValue(openweather);

        if (!aborted) {
          const nextState = {
            stripePublicKey: stripeVal,
            paypalClientId: paypalVal,
            openAIApiKey: openaiVal,
            swiklyApiKey: swiklyVal,
            openWeatherApiKey: openweatherVal,
            isLoading: false,
            error: null,
            hasStripeKey: !!stripeVal,
            hasPaypalKey: !!paypalVal,
            hasOpenAIKey: !!openaiVal,
            hasSwiklyKey: !!swiklyVal,
            hasOpenWeatherKey: !!openweatherVal,
          };
          setState(nextState);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: nextState }));
          } catch {
            // ignore cache write errors
          }
        }
      } catch (err) {
        console.error('Erreur chargement clés API:', err);
        if (!aborted) {
          setState((s) => ({ ...s, isLoading: false, error: 'Impossible de charger les clés API.' }));
        }
      }
    })();
    return () => { aborted = true; };
  }, []);

  return state;
}
